(function globalFoundation(window, document) {
  'use strict';

  var w = window;
  var d = document;
  if (!w || !d) return;

  // ---------------------------------------------------------------------------
  // Ownership
  // ---------------------------------------------------------------------------
  // global.js is foundation only.
  // It must not own API routing, point transactions, delivery generation,
  // third-party AI service bases, edge-worker bases, or product purchase execution.
  // Those belong to:
  // - assets/gcity-api-integration.js  -> GCityExecutor / transaction flow / compatibility facades
  // - assets/gfun-manager.js           -> point UX / balance / button gating / GFunPrice
  // - page or section assets           -> map/report/download UI
  // - product runtime asset            -> product-specific purchase orchestration

  // ---------------------------------------------------------------------------
  // Namespace foundation
  // ---------------------------------------------------------------------------

  var GCITY = w.GCITY = w.GCITY || {};
  w.LITTLEG_SERVER_DATA = w.LITTLEG_SERVER_DATA || {};

  GCITY.routes = GCITY.routes || {};
  GCITY.a11y = GCITY.a11y || {};
  GCITY.keyhub = GCITY.keyhub || {};
  GCITY.ui = GCITY.ui || {};
  GCITY.utils = GCITY.utils || {};
  GCITY.customer = GCITY.customer || {};
  GCITY.money = GCITY.money || {};

  GCITY.VERSION = GCITY.VERSION || 'global-foundation-v1';

  // ---------------------------------------------------------------------------
  // Safe environment metadata
  // ---------------------------------------------------------------------------
  // No external service bases or private execution endpoints in global.js.

  GCITY.ENV = Object.assign({}, GCITY.ENV || {}, {
    isDevelopment: w.location.hostname === 'localhost' || w.location.hostname.indexOf('myshopify.com') !== -1,
    isProduction: w.location.hostname === 'glhksolution.com' || w.location.hostname === 'www.glhksolution.com' || w.location.hostname === 'littleg.group' || w.location.hostname === 'www.littleg.group',
    DEBUG: !!(w.Shopify && w.Shopify.designMode) || w.location.hostname === 'localhost' || w.location.hostname.indexOf('myshopify.com') !== -1,
    hostname: w.location.hostname,
    origin: w.location.origin,
    shopDomain: (w.Shopify && w.Shopify.shop) || ''
  });

  GCITY.CONFIG = Object.assign({}, GCITY.CONFIG || {}, {
    VERSION: GCITY.VERSION,
    GFUN_REFRESH_INTERVAL: 30000,
    MAX_RETRIES: 3,
    RETRY_DELAY: 1000
  });

  // ---------------------------------------------------------------------------
  // Legacy map globals: compatibility only, no worker base ownership
  // ---------------------------------------------------------------------------

  w.ENV = w.ENV || { GMAPS_GLOBAL_KEY: '' };
  w.PROVIDERS = w.PROVIDERS || { LEAFLET: 'leaflet', GOOGLE: 'google' };
  w.DEFAULTS = Object.assign({}, w.DEFAULTS || {}, {
    mapContainerId: 'map',
    googleMapContainerId: 'map-pro'
  });
  delete w.DEFAULTS.baseUrl;

  w.MAPS = Object.assign({}, w.MAPS || {}, {
    PROVIDER: (w.MAPS && w.MAPS.PROVIDER) || w.PROVIDERS.LEAFLET,
    GOOGLE_API_KEY: (w.MAPS && w.MAPS.GOOGLE_API_KEY) || w.GCITY_GOOGLE_MAPS_KEY || ''
  });

  // ---------------------------------------------------------------------------
  // Selector / DOM / event utilities
  // ---------------------------------------------------------------------------

  function qs(selector, root) {
    return (root || d).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || d).querySelectorAll(selector));
  }

  function on(element, eventName, handler, options) {
    if (!element || !eventName || typeof handler !== 'function') return function noop() {};
    element.addEventListener(eventName, handler, options || false);
    return function off() {
      try { element.removeEventListener(eventName, handler, options || false); } catch (_) {}
    };
  }

  function once(element, key, binder) {
    if (!element || !key || typeof binder !== 'function') return false;
    var attr = 'data-gcity-bound-' + String(key).replace(/[^a-z0-9_-]/gi, '-').toLowerCase();
    if (element.getAttribute(attr) === 'true') return false;
    element.setAttribute(attr, 'true');
    binder(element);
    return true;
  }

  function ready(callback) {
    if (typeof callback !== 'function') return;
    if (d.readyState === 'loading') {
      d.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function debounce(fn, wait) {
    var timer = null;
    return function debounced() {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function run() {
        fn.apply(ctx, args);
      }, Number(wait || 300));
    };
  }

  function throttle(fn, limit) {
    var locked = false;
    return function throttled() {
      if (locked) return;
      locked = true;
      var ctx = this;
      var args = arguments;
      fn.apply(ctx, args);
      setTimeout(function unlock() {
        locked = false;
      }, Number(limit || 300));
    };
  }

  function emit(name, detail) {
    if (!name) return;
    d.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function getActiveCurrency(fallback) {
    var currency =
      (w.Shopify && w.Shopify.currency && w.Shopify.currency.active) ||
      (w.LITTLEG_SERVER_DATA && w.LITTLEG_SERVER_DATA.currency) ||
      (d.documentElement && d.documentElement.getAttribute('data-currency')) ||
      fallback ||
      'HKD';
    return String(currency || 'HKD').trim().toUpperCase();
  }

  function getActiveLocale(fallback) {
    var locale =
      (w.Shopify && w.Shopify.locale) ||
      (w.LITTLEG_SERVER_DATA && w.LITTLEG_SERVER_DATA.locale) ||
      (d.documentElement && d.documentElement.lang) ||
      fallback ||
      'en-HK';
    return String(locale || 'en-HK').trim() || 'en-HK';
  }

  function normalizeCurrencyAmount(value) {
    var amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  }

  function formatCurrency(amount, options) {
    options = options || {};
    var locale = String(options.locale || getActiveLocale('en-HK'));
    var currency = String(options.currency || getActiveCurrency('HKD')).toUpperCase();
    var formatterOptions = {
      style: 'currency',
      currency: currency,
      currencyDisplay: options.currencyDisplay || 'symbol'
    };

    if (Number.isInteger(options.minimumFractionDigits)) formatterOptions.minimumFractionDigits = options.minimumFractionDigits;
    if (Number.isInteger(options.maximumFractionDigits)) formatterOptions.maximumFractionDigits = options.maximumFractionDigits;

    try {
      return new Intl.NumberFormat(locale, formatterOptions).format(normalizeCurrencyAmount(amount));
    } catch (_) {
      try {
        return new Intl.NumberFormat('en-HK', {
          style: 'currency',
          currency: currency
        }).format(normalizeCurrencyAmount(amount));
      } catch (_) {
        return currency + ' ' + normalizeCurrencyAmount(amount).toFixed(2);
      }
    }
  }

  function formatCurrencyFromCents(cents, options) {
    return formatCurrency(normalizeCurrencyAmount(cents) / 100, options || {});
  }

  Object.assign(GCITY.money, {
    getActiveCurrency: GCITY.money.getActiveCurrency || getActiveCurrency,
    getActiveLocale: GCITY.money.getActiveLocale || getActiveLocale,
    format: GCITY.money.format || formatCurrency,
    formatCents: GCITY.money.formatCents || formatCurrencyFromCents
  });

  var storage = {
    set: function setStorage(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
    },
    get: function getStorage(key) {
      try { return JSON.parse(localStorage.getItem(key)); } catch (_) { return null; }
    },
    remove: function removeStorage(key) {
      try { localStorage.removeItem(key); } catch (_) {}
    }
  };

  function loadScript(src, id, opts) {
    opts = opts || {};
    return new Promise(function loadScriptPromise(resolve, reject) {
      if (!src) return reject(new Error('missing_script_src'));
      if (id && d.getElementById(id)) return resolve();

      var script = d.createElement('script');
      script.src = src;
      script.async = opts.async !== false;
      script.defer = opts.defer !== false;
      if (id) script.id = id;
      script.onload = function onLoad() { resolve(); };
      script.onerror = function onError() { reject(new Error('Failed to load script: ' + src)); };
      d.head.appendChild(script);
    });
  }

  function loadCSS(href, id) {
    return new Promise(function loadCssPromise(resolve, reject) {
      if (!href) return reject(new Error('missing_css_href'));
      if (id && d.getElementById(id)) return resolve();

      var link = d.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      if (id) link.id = id;
      link.onload = function onLoad() { resolve(); };
      link.onerror = function onError() { reject(new Error('Failed to load css: ' + href)); };
      d.head.appendChild(link);
    });
  }

  function getMetafieldPath(namespace, key) {
    return String(namespace || '') + '.' + String(key || '');
  }

  function computeMapHeight(aspectRatio, maxWidth) {
    var ratio = Number(aspectRatio || (16 / 9));
    var width = Math.min(w.innerWidth || 0, Number(maxWidth || 1200));
    if (!ratio || ratio <= 0) ratio = 16 / 9;
    return Math.floor(width / ratio);
  }

  Object.assign(GCITY.utils, {
    qs: GCITY.utils.qs || qs,
    qsa: GCITY.utils.qsa || qsa,
    on: GCITY.utils.on || on,
    once: GCITY.utils.once || once,
    ready: GCITY.utils.ready || ready,
    onReady: GCITY.utils.onReady || ready,
    debounce: GCITY.utils.debounce || debounce,
    throttle: GCITY.utils.throttle || throttle,
    emit: GCITY.utils.emit || emit,
    formatCurrency: GCITY.utils.formatCurrency || formatCurrency,
    formatCurrencyFromCents: GCITY.utils.formatCurrencyFromCents || formatCurrencyFromCents,
    getActiveCurrency: GCITY.utils.getActiveCurrency || getActiveCurrency,
    getActiveLocale: GCITY.utils.getActiveLocale || getActiveLocale,
    getMetafieldPath: GCITY.utils.getMetafieldPath || getMetafieldPath,
    computeMapHeight: GCITY.utils.computeMapHeight || computeMapHeight,
    storage: GCITY.utils.storage || storage,
    loadScript: GCITY.utils.loadScript || loadScript,
    loadCSS: GCITY.utils.loadCSS || loadCSS
  });

  GCITY.qs = GCITY.qs || GCITY.utils.qs;
  GCITY.qsa = GCITY.qsa || GCITY.utils.qsa;
  GCITY.onReady = GCITY.onReady || GCITY.utils.ready;
  GCITY.loadScript = GCITY.loadScript || GCITY.utils.loadScript;
  GCITY.loadCSS = GCITY.loadCSS || GCITY.utils.loadCSS;

  // Legacy short utility namespace expected by older map bundles.
  w.U = w.U || {};
  w.U.loadScript = w.U.loadScript || GCITY.utils.loadScript;
  w.U.loadCSS = w.U.loadCSS || GCITY.utils.loadCSS;

  // ---------------------------------------------------------------------------
  // Safe API compatibility wrappers
  // ---------------------------------------------------------------------------
  // global.js must not execute GCity API calls directly.
  // Use GCityExecutor for canonical endpoints.

  GCITY.api = GCITY.api || {};

  GCITY.api.execute = GCITY.api.execute || function executeViaGCity(ctx) {
    if (w.GCityExecutor && typeof w.GCityExecutor.execute === 'function') {
      return w.GCityExecutor.execute(ctx || {});
    }
    return Promise.reject(new Error('missing_gcity_executor'));
  };

  GCITY.api.route = GCITY.api.route || function routeViaGCity(ctx) {
    if (w.GCityExecutor && typeof w.GCityExecutor.route === 'function') {
      return w.GCityExecutor.route(ctx || {});
    }
    return '';
  };

  GCITY.utils.api = GCITY.utils.api || {};

  GCITY.utils.api.execute = GCITY.utils.api.execute || GCITY.api.execute;

  GCITY.utils.api.fetch = GCITY.utils.api.fetch || function deprecatedGlobalFetch(url, options) {
    var target = String(url || '');

    if (!target) {
      return Promise.reject(new Error('missing_url'));
    }

    if (target.indexOf('workers.dev') !== -1 || target.indexOf('/api/') === 0 || target.indexOf(w.location.origin + '/api/') === 0) {
      return Promise.reject(new Error('global_js_api_fetch_blocked_use_GCityExecutor'));
    }

    // Shopify native AJAX endpoints are allowed here for legacy UI utilities only.
    if (target === '/cart.js' || target === '/cart/add.js' || target.indexOf('/cart/') === 0) {
      return fetch(target, options || {}).then(function parseCartResponse(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      });
    }

    return Promise.reject(new Error('global_js_fetch_not_allowed'));
  };

  // ---------------------------------------------------------------------------
  // Customer runtime mirror
  // ---------------------------------------------------------------------------
  // Non-authoritative view only. Canonical truth is server-injected CUSTOMER_DATA.

  function syncCustomerFromServerData() {
    try {
      var cd = w.CUSTOMER_DATA && typeof w.CUSTOMER_DATA === 'object' ? w.CUSTOMER_DATA : null;
      if (!cd) return;

      GCITY.customer = GCITY.customer || {};

      if (typeof GCITY.customer.isLoggedIn === 'undefined') GCITY.customer.isLoggedIn = !!cd.isLoggedIn;
      if (typeof GCITY.customer.isGuest === 'undefined') GCITY.customer.isGuest = !cd.isLoggedIn;
      if (typeof GCITY.customer.id === 'undefined' && cd.id) GCITY.customer.id = String(cd.id);
      if (typeof GCITY.customer.email === 'undefined' && cd.email) GCITY.customer.email = cd.email;
      if (typeof GCITY.customer.subscriptionTier === 'undefined' && typeof cd.tier !== 'undefined') {
        GCITY.customer.subscriptionTier = cd.tier;
      }
      if (typeof cd.gfunBalance !== 'undefined') {
        if (typeof GCITY.customer.gfunBalance === 'undefined') GCITY.customer.gfunBalance = Number(cd.gfunBalance) || 0;
        if (typeof GCITY.customer.gfun_balance === 'undefined') GCITY.customer.gfun_balance = Number(cd.gfunBalance) || 0;
      }
      if (!GCITY.customerId && cd.id) GCITY.customerId = String(cd.id);
    } catch (_) {}
  }

  syncCustomerFromServerData();

  if (!GCITY.__gfunBalanceMirrorInstalled) {
    GCITY.__gfunBalanceMirrorInstalled = true;

    function mirrorGFunBalance(event) {
      var detail = event && event.detail ? event.detail : {};
      var balance = Number(detail.balance);
      if (!Number.isFinite(balance)) return;

      GCITY.customer = GCITY.customer || {};
      GCITY.customer.gfun_balance = balance;
      GCITY.customer.gfunBalance = balance;
    }

    w.addEventListener('gfun:updated', mirrorGFunBalance);
    d.addEventListener('gfun:updated', mirrorGFunBalance);
    d.addEventListener('gcity:gfun-updated', mirrorGFunBalance);
  }

  // ---------------------------------------------------------------------------
  // A11y: details/summary, focus trap, focus-visible support
  // ---------------------------------------------------------------------------

  GCITY.a11y.trapFocusHandlers = GCITY.a11y.trapFocusHandlers || {};

  function getFocusableElements(container) {
    if (!container) return [];
    return qsa(
      'summary,a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      container
    ).filter(function isVisible(el) {
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  function trapFocus(container, firstElement) {
    if (!container) return function noop() {};

    var focusable = getFocusableElements(container);
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (firstElement && typeof firstElement.focus === 'function') {
      firstElement.focus();
    } else if (first && typeof first.focus === 'function') {
      first.focus();
    }

    function handler(event) {
      if (!event || (event.key !== 'Tab' && event.code !== 'Tab')) return;
      if (!first || !last) return;

      if (event.shiftKey) {
        if (d.activeElement === first || !container.contains(d.activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (d.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    d.addEventListener('keydown', handler, true);

    return function releaseFocusTrap() {
      try { d.removeEventListener('keydown', handler, true); } catch (_) {}
    };
  }

  function removeTrapFocus(focusTarget) {
    if (focusTarget && typeof focusTarget.focus === 'function') {
      try { focusTarget.focus(); } catch (_) {}
    }
  }

  function initDetailsSummary() {
    qsa('[id^="Details-"] summary').forEach(function enhanceSummary(summary) {
      if (!summary || summary.dataset.gcityA11ySummary === 'true') return;
      summary.dataset.gcityA11ySummary = 'true';

      var details = summary.parentNode;
      if (!details) return;

      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', String(details.hasAttribute('open')));

      var panel = summary.nextElementSibling;
      if (panel && panel.id) summary.setAttribute('aria-controls', panel.id);

      summary.addEventListener('click', function onSummaryClick(event) {
        var currentDetails = event.currentTarget.closest('details');
        if (!currentDetails) return;
        var willOpen = !currentDetails.hasAttribute('open');
        event.currentTarget.setAttribute('aria-expanded', String(willOpen));
      });

      if (!summary.closest('header-drawer, menu-drawer')) {
        details.addEventListener('keyup', function onDetailsKeyup(event) {
          if (event.code !== 'Escape' && event.key !== 'Escape') return;
          var openDetails = summary.closest('details[open]');
          if (!openDetails) return;
          openDetails.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
          try { summary.focus(); } catch (_) {}
        });
      }
    });
  }

  function focusTriggersKeyboardModality(el) {
    if (!el) return false;
    var type = el.type;
    var tagName = el.tagName;
    if (tagName === 'INPUT' && ['text', 'search', 'url', 'tel', 'email', 'password', 'number', 'date'].indexOf(type) !== -1 && !el.readOnly) return true;
    if (tagName === 'TEXTAREA' && !el.readOnly) return true;
    if (el.isContentEditable) return true;
    return false;
  }

  function applyFocusVisiblePolyfill() {
    if (GCITY.a11y.__focusVisiblePolyfillInstalled) return;
    GCITY.a11y.__focusVisiblePolyfillInstalled = true;

    var hadKeyboardEvent = true;

    d.addEventListener('keydown', function onKeyDown(event) {
      if (event.metaKey || event.altKey || event.ctrlKey) return;
      hadKeyboardEvent = true;
    }, true);

    function onPointerDown() {
      hadKeyboardEvent = false;
    }

    d.addEventListener('mousedown', onPointerDown, true);
    d.addEventListener('touchstart', onPointerDown, true);

    d.addEventListener('focus', function onFocus(event) {
      if (!event.target) return;
      if (hadKeyboardEvent || focusTriggersKeyboardModality(event.target)) {
        event.target.classList.add('focus-visible');
        event.target.setAttribute('data-focus-visible-added', '');
      }
    }, true);

    d.addEventListener('blur', function onBlur(event) {
      if (!event.target || !event.target.hasAttribute('data-focus-visible-added')) return;
      event.target.classList.remove('focus-visible');
      event.target.removeAttribute('data-focus-visible-added');
    }, true);
  }

  function initFocusVisible() {
    try {
      d.querySelector(':focus-visible');
    } catch (_) {
      applyFocusVisiblePolyfill();
    }
  }

  GCITY.a11y.getFocusableElements = GCITY.a11y.getFocusableElements || getFocusableElements;
  GCITY.a11y.trapFocus = GCITY.a11y.trapFocus || trapFocus;
  GCITY.a11y.removeTrapFocus = GCITY.a11y.removeTrapFocus || removeTrapFocus;
  GCITY.a11y.initDetailsSummary = GCITY.a11y.initDetailsSummary || initDetailsSummary;
  GCITY.a11y.initFocusVisible = GCITY.a11y.initFocusVisible || initFocusVisible;
  GCITY.a11y.applyFocusVisiblePolyfill = GCITY.a11y.applyFocusVisiblePolyfill || applyFocusVisiblePolyfill;
  GCITY.a11y.focusTriggersKeyboardModality = GCITY.a11y.focusTriggersKeyboardModality || focusTriggersKeyboardModality;
  GCITY.a11y.init = GCITY.a11y.init || function initA11y() {
    GCITY.a11y.initDetailsSummary();
    GCITY.a11y.initFocusVisible();
  };

  w.trapFocus = w.trapFocus || GCITY.a11y.trapFocus;
  w.removeTrapFocus = w.removeTrapFocus || GCITY.a11y.removeTrapFocus;

  ready(function initGlobalA11y() {
    GCITY.a11y.init();
  });

  // ---------------------------------------------------------------------------
  // HTML update utility
  // ---------------------------------------------------------------------------

  GCITY.HTMLUpdateUtility = GCITY.HTMLUpdateUtility || {
    viewTransition: function viewTransition(oldNode, newContent, preProcessCallbacks, postProcessCallbacks) {
      if (!oldNode || !newContent || !oldNode.parentNode) return;

      preProcessCallbacks = preProcessCallbacks || [];
      postProcessCallbacks = postProcessCallbacks || [];

      preProcessCallbacks.forEach(function runPreProcess(callback) {
        if (typeof callback === 'function') callback(newContent);
      });

      var wrapper = d.createElement('div');
      wrapper.innerHTML = newContent.outerHTML || String(newContent);
      var newNode = wrapper.firstElementChild || wrapper.firstChild;
      if (!newNode) return;

      var uniqueKey = Date.now();
      qsa('[id], [form]', oldNode).forEach(function deconflictNode(el) {
        if (el.id) el.id = el.id + '-' + uniqueKey;
        if (el.getAttribute && el.getAttribute('form')) {
          el.setAttribute('form', el.getAttribute('form') + '-' + uniqueKey);
        }
      });

      oldNode.parentNode.insertBefore(newNode, oldNode);
      oldNode.style.display = 'none';

      postProcessCallbacks.forEach(function runPostProcess(callback) {
        if (typeof callback === 'function') callback(newNode);
      });

      setTimeout(function removeOldNode() {
        try { oldNode.remove(); } catch (_) {}
      }, 500);
    }
  };

  // Dawn / legacy compatibility aliases.
  // Keep aliases only; ownership remains GCITY.HTMLUpdateUtility / GCITY.a11y.
  w.HTMLUpdateUtility = w.HTMLUpdateUtility || GCITY.HTMLUpdateUtility;
  w.onKeyUpEscape = w.onKeyUpEscape || function onKeyUpEscape(event) {
    if (!event || (event.code !== 'Escape' && event.key !== 'Escape')) return;
    var details = event.target && event.target.closest ? event.target.closest('details[open]') : null;
    if (!details) return;
    var summary = details.querySelector('summary');
    details.removeAttribute('open');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      try { summary.focus(); } catch (_) {}
    }
  };

  // ---------------------------------------------------------------------------
  // Form validation ownership
  // ---------------------------------------------------------------------------
  // Form validation is intentionally excluded from global.js.
  // Canonical owner: assets/form-error-a11y.js.
  // Dynamic Gift Card owner: assets/recipient-form.js.
  // Do not reintroduce form[data-validate] or GCITY.forms validation here.

  // ---------------------------------------------------------------------------
  // Shopify helpers: compatibility only
  // ---------------------------------------------------------------------------

  GCITY.shopify = GCITY.shopify || {};

  GCITY.shopify.bind = GCITY.shopify.bind || function bind(fn, scope) {
    return function boundFunction() { return fn.apply(scope, arguments); };
  };

  GCITY.shopify.setSelectorByValue = GCITY.shopify.setSelectorByValue || function setSelectorByValue(selector, value) {
    if (!selector || !selector.options) return undefined;
    for (var i = 0; i < selector.options.length; i += 1) {
      var option = selector.options[i];
      if (String(value) === String(option.value) || String(value) === String(option.innerHTML)) {
        selector.selectedIndex = i;
        return i;
      }
    }
    return undefined;
  };

  GCITY.shopify.addListener = GCITY.shopify.addListener || function addListener(target, eventName, callback) {
    if (!target || !eventName || typeof callback !== 'function') return;
    if (target.addEventListener) target.addEventListener(eventName, callback, false);
    else if (target.attachEvent) target.attachEvent('on' + eventName, callback);
  };

  GCITY.shopify.postLink = GCITY.shopify.postLink || function postLink(path, options) {
    options = options || {};
    var method = options.method || 'post';
    var params = options.parameters || {};
    var form = d.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    Object.keys(params).forEach(function appendField(key) {
      var field = d.createElement('input');
      field.setAttribute('type', 'hidden');
      field.setAttribute('name', key);
      field.setAttribute('value', params[key]);
      form.appendChild(field);
    });

    d.body.appendChild(form);
    form.submit();
    try { d.body.removeChild(form); } catch (_) {}
  };

  GCITY.shopify.CountryProvinceSelector = GCITY.shopify.CountryProvinceSelector || function CountryProvinceSelector(countryDomId, provinceDomId, options) {
    options = options || {};
    this.countryEl = d.getElementById(countryDomId);
    this.provinceEl = d.getElementById(provinceDomId);
    this.provinceContainer = d.getElementById(options.hideElement || provinceDomId);

    if (this.countryEl && this.provinceEl) {
      this.countryEl.addEventListener('change', this.countryHandler.bind(this));
      this.initCountry();
      this.initProvince();
    }
  };

  if (!GCITY.shopify.CountryProvinceSelector.prototype.__gcityEnhanced) {
    Object.assign(GCITY.shopify.CountryProvinceSelector.prototype, {
      __gcityEnhanced: true,

      initCountry: function initCountry() {
        var value = this.countryEl && this.countryEl.getAttribute('data-default');
        GCITY.shopify.setSelectorByValue(this.countryEl, value);
        this.countryHandler();
      },

      initProvince: function initProvince() {
        var value = this.provinceEl && this.provinceEl.getAttribute('data-default');
        if (value && this.provinceEl.options.length > 0) {
          GCITY.shopify.setSelectorByValue(this.provinceEl, value);
        }
      },

      countryHandler: function countryHandler() {
        if (!this.countryEl || !this.provinceEl) return;
        var opt = this.countryEl.options[this.countryEl.selectedIndex];
        if (!opt) return;

        var raw = opt.getAttribute('data-provinces') || '[]';
        var provinces = [];
        try { provinces = JSON.parse(raw); } catch (_) { provinces = []; }

        this.provinceEl.innerHTML = '';
        if (!provinces || provinces.length === 0) {
          if (this.provinceContainer) this.provinceContainer.style.display = 'none';
          return;
        }

        provinces.forEach(function appendProvince(pair) {
          var code = pair[0];
          var name = pair[1];
          var option = d.createElement('option');
          option.value = code;
          option.innerHTML = name;
          this.provinceEl.appendChild(option);
        }, this);

        if (this.provinceContainer) this.provinceContainer.style.display = '';
      }
    });
  }

  GCITY.shopify.init = GCITY.shopify.init || function initShopifyHelpers() {
    w.Shopify = w.Shopify || {};
    w.Shopify.bind = w.Shopify.bind || GCITY.shopify.bind;
    w.Shopify.setSelectorByValue = w.Shopify.setSelectorByValue || GCITY.shopify.setSelectorByValue;
    w.Shopify.addListener = w.Shopify.addListener || GCITY.shopify.addListener;
    w.Shopify.postLink = w.Shopify.postLink || GCITY.shopify.postLink;
    w.Shopify.CountryProvinceSelector = w.Shopify.CountryProvinceSelector || GCITY.shopify.CountryProvinceSelector;
  };

  GCITY.shopify.init();

  // ---------------------------------------------------------------------------
  // UI fallback only
  // ---------------------------------------------------------------------------

  GCITY.ui.toast = GCITY.ui.toast || function toast(message, type) {
    var payload = {
      message: String(message || ''),
      type: type || 'info'
    };
    emit('gcity:toast', payload);
    if (GCITY.ENV && GCITY.ENV.isDevelopment && w.console && typeof w.console.log === 'function') {
      w.console.log('[GCITY toast]', payload.type, payload.message);
    }
  };

  // ---------------------------------------------------------------------------
  // Metadata constants only
  // ---------------------------------------------------------------------------
  // No executable API endpoints in global constants.

  GCITY.Constants = Object.assign({}, GCITY.Constants || {}, {
    METAFIELD_NAMESPACES: {
      CUSTOM: 'custom',
      GCITY: 'gcity',
      LITTLEG: 'littleg',
      GFUN: 'gfun',
      RICS: 'rics',
      SHOPIFY: 'shopify'
    },

    METAFIELD_KEYS: {
      PRODUCT: {
        GFUN_CONSUME: 'custom.gfun_consume_per_call',
        DELIVERY_METHOD: 'custom.delivery_method',
        SERVICE_TYPE: 'gcity.service_type',
        RICS_LEVEL: 'gcity.rics_level',
        API_TRIGGER: 'custom.api_trigger_enabled',
        PRODUCT_CATEGORY: 'gcity.product_category'
      },
      CUSTOMER: {
        GFUN_BALANCE: 'custom.gfun_balance',
        unlockedIslands: 'custom.unlockedIslands_json',
        ACHIEVEMENTS: 'custom.achievements',

        VOICE_HISTORY: 'custom.voice_history',
        SUBSCRIPTION_TIER: 'gcity.subscription_tier',
      },
      COLLECTION: {
        SHOW_CHAPTER_NAV: 'custom.show_chapter_nav',
        CHAPTER_NAV_STYLE: 'custom.chapter_nav_style',
        HERO_BG_COLOR: 'custom.hero_background_color'
      },
      PAGE: {
        HERO_VIDEO_ID: 'custom.hero_video_id',
        STORY_MEMORY_VAULT: 'custom.story_memory_vault',
        AUDIO_VISUAL_SYNC: 'custom.audio_visual_sync'
      }
    },

    METAOBJECT_TYPES: {
      ISLAND: 'littleg_island',
      VOICE_DIARY: 'littleg_voice_diary_entry',
      gameMission: 'gameMission',
      achievementBadge: 'achievementBadge',
      KNOWLEDGE_ARTICLE: 'knowledge_article'
    },

    ISLAND_FIELDS: {
      NAME: 'islandName',
      LEVEL_REQUIRED: 'islandLevelRequired',
      THEME: 'island_theme',
      ICON_URL: 'island_icon_url',
      COLOR: 'island_color',
      UNLOCK_REQUIREMENT: 'unlock_requirement',
      GFUN_REWARD: 'gfun_reward',
      GFUN_CONSUME: 'gfun_consume_per_play',
      LEARNING_EVIDENCE: 'learning_evidence_type'
    },

    DELIVERY_METHODS: {
      PDF: 'pdf',
      EMAIL: 'email',
      API: 'api',
      INSTANT: 'instant',
      GIFT_CARD: 'gift_card'
    },

    DELIVERY_STATUS: {
      PENDING: 'pending',
      PROCESSING: 'processing',
      COMPLETED: 'completed',
      FAILED: 'failed'
    }
  });

  // ---------------------------------------------------------------------------
  // Performance monitoring: foundation instrumentation only
  // ---------------------------------------------------------------------------

  GCITY.performance = GCITY.performance || {
    mark: function mark(name) {
      if (!w.performance || typeof w.performance.mark !== 'function') return;
      var markName = 'gcity:' + String(name || 'unnamed');
      try {
        w.performance.mark(markName);
        if (GCITY.ENV && GCITY.ENV.DEBUG && w.console) w.console.log('⏱️ Mark:', name);
      } catch (_) {}
    },

    measure: function measure(name, startMark, endMark) {
      if (!w.performance || typeof w.performance.measure !== 'function') return;
      var measureName = 'gcity:' + String(name || 'unnamed');
      var start = 'gcity:' + String(startMark || 'start');
      var end = endMark ? 'gcity:' + String(endMark) : undefined;
      try {
        w.performance.measure(measureName, start, end);
        var entries = w.performance.getEntriesByName(measureName);
        if (entries.length > 0 && GCITY.ENV && GCITY.ENV.DEBUG && w.console) {
          w.console.log('📊 Measure [' + name + ']:', entries[entries.length - 1].duration.toFixed(2) + 'ms');
        }
      } catch (_) {}
    }
  };

  // ---------------------------------------------------------------------------
  // Dashboard routing utils: no auto redirect
  // ---------------------------------------------------------------------------

  var GCITY_VIEW_MAP = {
    kid: 'kid',
    parent: 'parent',
    pro: 'pro',
    gcitypartners: 'pro',
    partner: 'pro',
    littleg: 'parent'
  };

  var GCITY_ALLOWED_VIEWS = ['kid', 'parent', 'pro'];

  function gcityNormalizeView(raw) {
    var key = String(raw || '').trim().toLowerCase();
    return GCITY_VIEW_MAP[key] || 'parent';
  }

  function gcitySetPendingView(view) {
    var normalized = gcityNormalizeView(view);
    try { localStorage.setItem('gcity_pending_view', normalized); } catch (_) {}
  }

  function gcityConsumePendingView() {
    var view = null;
    try {
      view = localStorage.getItem('gcity_pending_view');
      localStorage.removeItem('gcity_pending_view');
    } catch (_) {}
    return GCITY_ALLOWED_VIEWS.indexOf(view) !== -1 ? view : null;
  }

  function gcityDashboardUrl(view) {
    return '/pages/littleg-dashboard?view=' + encodeURIComponent(gcityNormalizeView(view));
  }

  w.gcityNormalizeView = w.gcityNormalizeView || gcityNormalizeView;
  w.gcitySetPendingView = w.gcitySetPendingView || gcitySetPendingView;
  w.gcityConsumePendingView = w.gcityConsumePendingView || gcityConsumePendingView;
  w.gcityDashboardUrl = w.gcityDashboardUrl || gcityDashboardUrl;

  GCITY.redirect = GCITY.redirect || {};
  GCITY.redirect.normalizeView = GCITY.redirect.normalizeView || gcityNormalizeView;
  GCITY.redirect.setPendingView = GCITY.redirect.setPendingView || gcitySetPendingView;
  GCITY.redirect.consumePendingView = GCITY.redirect.consumePendingView || gcityConsumePendingView;
  GCITY.redirect.dashboardUrl = GCITY.redirect.dashboardUrl || gcityDashboardUrl;
  GCITY.redirect.init = GCITY.redirect.init || function noopRedirectInit() {};

  // ---------------------------------------------------------------------------
  // Foundation bootstrapping
  // ---------------------------------------------------------------------------
  // No product purchase init, no GFun update, no cart init, no map destroy here.
  // Domain runtimes own their own boot behavior.

  function onSectionLoad(event) {
    var sectionId = event && event.detail ? event.detail.sectionId : '';
    var sectionRoot = sectionId ? d.getElementById('shopify-section-' + sectionId) : null;

    if (GCITY.ui && typeof GCITY.ui.init === 'function') GCITY.ui.init();

    if (GCITY.a11y && typeof GCITY.a11y.init === 'function') GCITY.a11y.init();

    d.dispatchEvent(new CustomEvent('gcity:section:load', {
      detail: {
        sectionId: sectionId,
        sectionRoot: sectionRoot,
        source: 'global.js'
      }
    }));
  }

  function onSectionUnload(event) {
    var sectionId = event && event.detail ? event.detail.sectionId : '';
    var sectionRoot = sectionId ? d.getElementById('shopify-section-' + sectionId) : null;

    d.dispatchEvent(new CustomEvent('gcity:section:unload', {
      detail: {
        sectionId: sectionId,
        sectionRoot: sectionRoot,
        source: 'global.js'
      }
    }));
  }

  function boot() {
    if (GCITY.__foundationBooted) return;
    GCITY.__foundationBooted = true;

    if (GCITY.performance) GCITY.performance.mark('foundation_boot_start');

    if (GCITY.shopify && typeof GCITY.shopify.init === 'function') GCITY.shopify.init();
    if (GCITY.a11y && typeof GCITY.a11y.init === 'function') GCITY.a11y.init();


    if (GCITY.performance) {
      GCITY.performance.mark('foundation_boot_end');
      GCITY.performance.measure('foundation_boot_duration', 'foundation_boot_start', 'foundation_boot_end');
    }

    d.documentElement.classList.add('gcity-ready');
    d.dispatchEvent(new CustomEvent('gcity:ready', {
      detail: {
        version: GCITY.VERSION,
        source: 'global.js'
      }
    }));
  }

  d.removeEventListener('shopify:section:load', onSectionLoad);
  d.addEventListener('shopify:section:load', onSectionLoad);
  d.removeEventListener('shopify:section:unload', onSectionUnload);
  d.addEventListener('shopify:section:unload', onSectionUnload);

  GCITY.core = GCITY.core || {};
  GCITY.core.version = GCITY.core.version || GCITY.VERSION;
  GCITY.core.boot = GCITY.core.boot || boot;
  GCITY.core.reload = GCITY.core.reload || onSectionLoad;

  ready(boot);

  // ---------------------------------------------------------------------------
  // Boundary reminder
  // ---------------------------------------------------------------------------
  // Do not re-add business execution, purchase orchestration, delivery generation,
  // external service bases, edge-worker bases, or direct private API constants here.

  emit('gcity:global-ready', {
    version: GCITY.VERSION,
    source: 'global.js'
  });
})(window, document);
